import type {
   CreateUserProfileBody,
   UserProfile,
   UserWithProfileAndApiKey,
} from '@/types/user/user-profile-types.ts';
import { ref } from 'vue';

import { ApiFetchError, useApiFetch } from '@/services/api-client.ts';
import { useAuthStore } from '@/stores/auth-store.ts';

/* ---------------------------------------------------------------------------------------------- */

type FetchUserProfileReturn = 200 | 404 | 500;

/* ---------------------------------------------------------------------------------------------- */

/**
 * Composable for handling user profile
 */
export function useUserProfile() {
   const authStore = useAuthStore();
   const userProfile = ref<UserProfile | null>(null);

   const isLoading = ref(false);

   const error = ref<string | null>(null);

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Fetch the user profile information
    */
   const fetchUserProfile = async (forced: boolean = false): Promise<FetchUserProfileReturn> => {
      try {
         isLoading.value = true;
         error.value = null;

         /*
          * Get user profile info if it is available in auth-store
          */
         if (!forced) {
            if (authStore.userProfile) {
               userProfile.value = authStore.userProfile;
               return 200;
            }
         }

         const response = await useApiFetch<UserWithProfileAndApiKey>(`/auth/user/me`);

         if (!response) {
            error.value = 'Profile not found';
            return 404;
         }

         userProfile.value = response.profile;
         authStore.demoRequest = response.demo_request;

         // save user, user profile & api key in auth-store
         authStore.user = response.user;
         authStore.userProfile = userProfile.value;
         authStore.userApiKey = response.api_key;
         authStore.maintenanceMode = response.maintenance_mode;

         return 200;
      }
      catch (e) {
         if (e instanceof ApiFetchError) {
            error.value = e.message;
            return e.status === 404 ? 404 : 500;
         }
         else {
            error.value = 'Failed to fetch user profile';
            return 500;
         }
      }
      finally {
         isLoading.value = false;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Create the user profile information
    */
   const createUserProfile = async (profile: CreateUserProfileBody) => {
      try {
         isLoading.value = true;
         error.value = null;

         const response = await useApiFetch('/users/profile', {
            method: 'POST',
            body: {
               ...profile,
            },
         });

         if (!response) {
            error.value = 'Failed to create profile';
            return false;
         }

         userProfile.value = response;

         authStore.userProfile = userProfile.value;

         return true;
      }
      catch (e) {
         if (e instanceof ApiFetchError) {
            error.value = e.message;
         }
         return false;
      }
      finally {
         isLoading.value = false;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Save the user profile information
    */
   const updateUserProfile = async (profile: UserProfile) => {
      try {
         isLoading.value = true;
         error.value = null;

         const response = await useApiFetch('/users/profile', {
            method: 'PUT',
            body: {
               ...profile,
            },
         });

         if (!response) {
            error.value = 'Failed to save profile';
            return false;
         }

         userProfile.value = response;

         authStore.userProfile = userProfile.value;

         return true;
      }
      catch (e) {
         if (e instanceof ApiFetchError) {
            error.value = e.message;
         }
         return false;
      }
      finally {
         isLoading.value = false;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Generate api_key for the user
    */
   async function generateApiKey() {
      try {
         isLoading.value = true;
         error.value = null;

         const response = await useApiFetch('/users/generate_api_key', {
            method: 'POST',
            body: {
               access_token: authStore.accessToken,
            },
         });

         if (!response) {
            error.value = 'Failed to generate api key';
            return false;
         }

         return true;
      }
      catch (e) {
         if (e instanceof ApiFetchError) {
            error.value = e.message;
         }
         return false;
      }
      finally {
         isLoading.value = false;
      }
   }

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Init user profile, if there is no api key, new key will be generated along with
    * user profile
    */
   async function initUserProfile() {
      const statusCode = await fetchUserProfile(true);

      if (statusCode !== 200) {
         throw new Error('Failed to init user profile');
      }
   }

   /* ------------------------------------------------------------------------------------------- */

   return {
      userProfile,
      isLoading,
      error,

      fetchUserProfile,
      updateUserProfile,
      createUserProfile,
      initUserProfile,

      generateApiKey,
   };
}
